import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X, Shield, Crown, ShoppingCart, Play, Pause, Plus, Check, Music, Sparkles, ChevronDown, Lock, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CatalogPage = () => {
  const { t, language } = useLanguage();
  const isEs = language === 'es';
  const isPt = language === 'pt';
  const tx = (es, en, pt) => isEs ? es : (isPt ? (pt || en) : en);
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [vibes, setVibes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ genre: '', vibe: '', search: '', bpm: '', price: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const TRACKS_PER_PAGE = 48;

  // Cart
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Audio player
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // Track detail popup
  const [selectedTrack, setSelectedTrack] = useState(null);

  // Active promotion
  const [promo, setPromo] = useState(null);
  const [promoTimeLeft, setPromoTimeLeft] = useState('');
  const [showPromoBanner, setShowPromoBanner] = useState(true);

  useEffect(() => { fetchData(); fetchPromo(); }, []);
  useEffect(() => { setPage(1); fetchTracks(1); }, [filters.genre, filters.vibe]);

  // Auto-refresh catalog every 10 seconds to show newly approved tracks
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTracks(1, true);
    }, 10000);
    return () => clearInterval(interval);
  }, [filters.genre, filters.vibe]);

  const fetchData = async () => {
    try {
      const [genresRes, vibesRes] = await Promise.all([
        axios.get(`${API_URL}/api/tracks/genres/list`),
        axios.get(`${API_URL}/api/tracks/vibes/list`)
      ]);
      setGenres(genresRes.data);
      setVibes(vibesRes.data);
    } catch { }
  };

  const fetchPromo = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/active-promotion`);
      if (r.data.active) { setPromo(r.data); setShowPromoBanner(true); }
    } catch {}
  };

  useEffect(() => {
    if (!promo?.expires_at) return;
    const update = () => {
      const diff = new Date(promo.expires_at) - new Date();
      if (diff <= 0) { setPromoTimeLeft(''); setPromo(null); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setPromoTimeLeft(d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`);
    };
    update();
    const iv = setInterval(update, 60000);
    return () => clearInterval(iv);
  }, [promo]);

  const fetchTracks = async (p, silent) => {
    const pg = p || page;
    if (pg === 1 && !silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.vibe) params.append('vibe', filters.vibe);
      params.append('page', pg);
      params.append('limit', TRACKS_PER_PAGE);
      const response = await axios.get(`${API_URL}/api/tracks/?${params.toString()}`);
      if (pg === 1) setTracks(response.data);
      else setTracks(prev => [...prev, ...response.data]);
      setHasMore(response.data.length >= TRACKS_PER_PAGE);
    } catch { }
    if (!silent) setLoading(false);
  };

  const loadMore = () => { const next = page + 1; setPage(next); fetchTracks(next); };

  const filteredTracks = tracks.filter(track => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!track.title?.toLowerCase().includes(q) && !track.artist?.toLowerCase().includes(q) && !track.genre?.toLowerCase().includes(q)) return false;
    }
    if (filters.bpm) {
      const bpmRange = filters.bpm;
      if (bpmRange === 'slow' && (track.bpm < 60 || track.bpm > 90)) return false;
      if (bpmRange === 'medium' && (track.bpm < 91 || track.bpm > 120)) return false;
      if (bpmRange === 'fast' && (track.bpm < 121 || track.bpm > 150)) return false;
      if (bpmRange === 'very-fast' && track.bpm < 151) return false;
    }
    if (filters.price) {
      if (filters.price === 'under5' && track.price >= 5) return false;
      if (filters.price === '5to10' && (track.price < 5 || track.price > 10)) return false;
      if (filters.price === 'over10' && track.price <= 10) return false;
    }
    return true;
  });

  const clearFilters = () => setFilters({ genre: '', vibe: '', search: '', bpm: '', price: '' });
  const hasActiveFilters = filters.genre || filters.vibe || filters.search || filters.bpm || filters.price;

  const togglePlay = (track) => {
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const url = track.audio_preview_url?.startsWith('/') ? `${API_URL}${track.audio_preview_url}` : track.audio_preview_url;
      if (url) {
        audioRef.current = new Audio(url);
        audioRef.current.play().catch(() => {});
        audioRef.current.onended = () => setPlayingId(null);
        setPlayingId(track.id);
      }
    }
  };

  const addToCart = (track) => {
    if (cart.find(c => c.id === track.id)) return;
    setCart(prev => [...prev, track]);
    setSelectedTrack(null);
  };

  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const isInCart = (id) => cart.some(c => c.id === id);

  const handleCheckout = () => {
    if (cart.length === 1) {
      navigate(`/checkout?type=track&id=${cart[0].id}&license=basic`);
    } else if (cart.length > 1) {
      // Multi-item cart: pass IDs as comma-separated
      const ids = cart.map(c => c.id).join(',');
      navigate(`/checkout?type=cart&ids=${ids}&license=basic`);
    }
  };

  return (
    <div className="min-h-screen pt-24">
      <div className="container-custom py-12">
        {/* Promotion Banner */}
        {promo && showPromoBanner && (
          <div className="mb-8 relative overflow-hidden border-t border-b border-[#E8720C]/15 py-4" data-testid="promo-banner" style={{ background: 'linear-gradient(90deg, rgba(232,114,12,0.06) 0%, transparent 50%, rgba(232,114,12,0.03) 100%)' }}>
            <button onClick={() => setShowPromoBanner(false)} className="absolute top-3 right-3 p-1 text-white/15 hover:text-white/40 z-10">
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center justify-center gap-6">
              <div className="flex items-center gap-2">
                <img src="https://customer-assets.emergentagent.com/job_og-catalog-pro/artifacts/qr8unnx1_logo%20ovalle%201.png"
                  alt="OG Music" className="h-7 w-7 object-contain rounded brightness-90" style={{ mixBlendMode: 'lighten' }} />
                <span className="text-lg font-bold tracking-tight">
                  <span className="text-white">OG</span>
                  <span className="gold-text ml-1">MUSIC</span>
                </span>
              </div>
              <div className="w-[1px] h-8 bg-white/10" />
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-[#E8720C] tracking-tight">{promo.discount_pct}%</span>
                <span className="text-sm font-bold text-[#E8720C] uppercase">OFF</span>
              </div>
              <div className="w-[1px] h-8 bg-white/10" />
              <div>
                <p className="text-sm font-bold text-white tracking-wide">{promo.banner_text}</p>
                <p className="text-[10px] text-white/30 uppercase tracking-[0.15em]">
                  {promo.scope === 'genre' ? promo.genres?.join(' / ') : promo.scope === 'all' ? 'Todo el catalogo' : 'Tracks seleccionados'}
                  {promoTimeLeft && <span className="text-[#E8720C] ml-3">{promoTimeLeft}</span>}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8720C] mb-3">{tx('Ecosistema Musical Completo', 'Complete Music Ecosystem', 'Ecossistema Musical Completo')}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
            {t('catalog.title')} <span className="gold-text">{t('catalog.titleHighlight')}</span>
          </h1>
          <p className="text-base text-white/50 max-w-2xl">
            {tx(
              'Cada track incluye: Música en WAV + MP3, Portada OG exclusiva, Licencia certificada y Guía de distribución. Un ecosistema completo con un solo clic.',
              'Each track includes: Music in WAV + MP3, Exclusive OG cover, Certified license and Distribution guide. A complete ecosystem with one click.',
              'Cada track inclui: Música em WAV + MP3, Capa OG exclusiva, Licença certificada e Guia de distribuição. Um ecossistema completo com um clique.'
            )}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-green-400 font-bold" data-testid="pre-mastered-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
            {tx('Pre-masterizado profesionalmente · Listo para Spotify', 'Professionally pre-mastered · Spotify-ready', 'Pré-masterizado profissionalmente · Pronto para Spotify')}
          </div>
        </div>

        {/* Ecosystem Banner */}
        <div className="glass-card p-5 mb-8 border border-[#E8720C]/10 bg-gradient-to-r from-[#E8720C]/5 to-transparent" data-testid="ecosystem-banner">
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="flex flex-wrap gap-5">
              {[
                { icon: Music, label: 'WAV + MP3', desc: tx('2 formatos', '2 formats', '2 formatos') },
                { icon: Sparkles, label: tx('Portada OG', 'OG Cover', 'Capa OG'), desc: tx('Exclusiva para ti', 'Exclusive for you', 'Exclusiva para você') },
                { icon: Shield, label: tx('Licencia', 'License', 'Licença'), desc: tx('Certificada', 'Certified', 'Certificada') },
                { icon: Crown, label: tx('Guía', 'Guide', 'Guia'), desc: tx('Distribución', 'Distribution', 'Distribuição') },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#E8720C]/10 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-[#E8720C]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{item.label}</p>
                    <p className="text-[10px] text-white/40">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/40 max-w-xs">{tx('Al comprar, genera tu ', 'On purchase, generate your ', 'Ao comprar, gere sua ')}<strong className="text-[#E8720C]">{tx('Portada OG', 'OG Cover', 'Capa OG')}</strong>{tx(' desde tu panel. Una portada única, solo para ti.', ' from your dashboard. A unique cover, just for you.', ' do seu painel. Uma capa única, só para você.')}</p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="glass-card p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input type="text" placeholder={tx('Buscar por nombre, artista o género...', 'Search by name, artist or genre...', 'Buscar por nome, artista ou gênero...')}
                value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
                className="w-full input-glass pl-11 text-sm" data-testid="catalog-search" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden btn-secondary py-2.5 flex items-center justify-center gap-2 text-sm">
              <Filter className="w-4 h-4" />{tx('Filtros', 'Filters', 'Filtros')}
              {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-[#E8720C]" />}
            </button>
            <div className={`flex flex-col sm:flex-row gap-3 ${showFilters ? 'block' : 'hidden lg:flex'}`}>
              <Select value={filters.genre || "all"} onValueChange={v => setFilters({ ...filters, genre: v === "all" ? "" : v })}>
                <SelectTrigger className="w-full sm:w-[160px] bg-white/5 border-white/10 text-sm" data-testid="genre-filter"><SelectValue placeholder={tx('Género', 'Genre', 'Gênero')} /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] border-white/10">
                  <SelectItem value="all">{tx('Todos los Géneros', 'All Genres', 'Todos os Gêneros')}</SelectItem>
                  {genres.filter(g => g?.trim()).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.vibe || "all"} onValueChange={v => setFilters({ ...filters, vibe: v === "all" ? "" : v })}>
                <SelectTrigger className="w-full sm:w-[160px] bg-white/5 border-white/10 text-sm" data-testid="vibe-filter"><SelectValue placeholder={tx('Sub Género', 'Sub Genre', 'Sub Gênero')} /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] border-white/10">
                  <SelectItem value="all">{tx('Todos los Sub Géneros', 'All Sub Genres', 'Todos os Sub Gêneros')}</SelectItem>
                  {vibes.filter(v => v?.trim()).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.bpm || "all"} onValueChange={v => setFilters({ ...filters, bpm: v === "all" ? "" : v })}>
                <SelectTrigger className="w-full sm:w-[130px] bg-white/5 border-white/10 text-sm" data-testid="bpm-filter"><SelectValue placeholder="BPM" /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] border-white/10">
                  <SelectItem value="all">{tx('Todos los BPM', 'All BPM', 'Todos os BPM')}</SelectItem>
                  <SelectItem value="slow">60-90</SelectItem>
                  <SelectItem value="medium">91-120</SelectItem>
                  <SelectItem value="fast">121-150</SelectItem>
                  <SelectItem value="very-fast">151+</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="btn-secondary py-2 px-3 flex items-center gap-1 text-sm" data-testid="clear-filters">
                  <X className="w-3.5 h-3.5" />{tx('Limpiar', 'Clear', 'Limpar')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mastering Trust Badge — All catalog tracks are pre-mastered */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 mb-3 rounded-xl bg-gradient-to-r from-green-500/15 via-emerald-500/8 to-transparent border border-green-500/25"
          data-testid="catalog-mastering-badge">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-green-500/20 flex items-center justify-center shrink-0">
              <Check className="w-3.5 h-3.5 text-green-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-green-200">Todos los tracks YA están masterizados profesionalmente</p>
              <p className="text-[10px] text-white/50 truncate">
                Calidad estudio (-14 LUFS · WAV 24-bit) · <strong className="text-green-300">Listo para Spotify, Apple Music y radio</strong>
              </p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-500/20 text-green-300 text-[10px] font-bold tracking-wider">
            PRO
          </span>
        </div>

        {/* Preview Notice — Anti-piracy branding as a sales argument */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 mb-3 rounded-xl bg-gradient-to-r from-[#E8720C]/10 via-[#E8720C]/5 to-transparent border border-[#E8720C]/15"
          data-testid="preview-notice">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#E8720C]/15 flex items-center justify-center shrink-0">
              <Lock className="w-3.5 h-3.5 text-[#E8720C]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white/90">Preview de 60 segundos</p>
              <p className="text-[10px] text-white/50 truncate">
                Compra para obtener la cancion completa en <strong className="text-[#E8720C]">WAV + MP3</strong> sin marcas
              </p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E8720C]/15 text-[#E8720C] text-[10px] font-bold tracking-wider">
            1:00
          </span>
        </div>

        {/* Packs Cross-sell Banner — promote bundles for bigger savings */}
        <button
          onClick={() => navigate('/packs')}
          className="w-full group relative overflow-hidden text-left px-4 py-3 mb-4 rounded-xl bg-gradient-to-r from-[#E8720C]/15 via-orange-500/10 to-[#E8720C]/5 border border-[#E8720C]/30 hover:border-[#E8720C]/60 transition-all"
          data-testid="packs-cross-sell-banner">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#E8720C] to-[#C55A00] flex items-center justify-center shrink-0">
                <ShoppingCart className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold flex items-center gap-2 flex-wrap">
                  Compra en Pack y ahorra hasta <span className="text-[#E8720C]">40%</span>
                  <span className="hidden sm:inline-flex px-1.5 py-0.5 bg-[#E8720C] text-white text-[9px] font-bold rounded uppercase tracking-wider animate-pulse">Hot</span>
                </p>
                <p className="text-[10px] text-white/60 truncate">
                  Pack 5 · Pack 10 · Pack 20 — Tracks YA masterizados pro (WAV + MP3 + portada + licencia)
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#E8720C] group-hover:bg-[#C55A00] text-white rounded-lg text-xs font-bold shrink-0 transition-colors">
              Ver Packs <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </button>

        {/* Results + Cart Bar */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-white/40 text-sm">{filteredTracks.length} tracks encontrados</p>
          {cart.length > 0 && (
            <button onClick={() => setShowCart(!showCart)}
              className="flex items-center gap-2 px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white transition-colors" data-testid="cart-toggle">
              <ShoppingCart className="w-4 h-4" />{cart.length} en carrito
            </button>
          )}
        </div>

        {/* Cart Dropdown */}
        {showCart && cart.length > 0 && (
          <div className="glass-card p-5 mb-6 border border-[#E8720C]/20" data-testid="cart-panel">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-[#E8720C]" />Tu Carrito ({cart.length})
            </h3>
            <div className="space-y-2 mb-4">
              {cart.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg" data-testid={`cart-item-${item.id}`}>
                  <div className="flex items-center gap-3">
                    <Music className="w-4 h-4 text-white/30" />
                    <div>
                      <p className="text-sm font-medium">{item.title}</p>
                      <p className="text-[10px] text-white/40">{item.artist} {item.genre && `| ${item.genre}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#E8720C]">${item.price}</span>
                    <button onClick={() => removeFromCart(item.id)} className="p-1 hover:bg-white/10 rounded text-white/30 hover:text-red-400">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <div>
                <p className="text-xs text-white/40">Total estimado</p>
                <p className="text-lg font-bold text-[#E8720C]">${cart.reduce((s, c) => s + (c.price || 0), 0).toFixed(2)}</p>
              </div>
              <button onClick={handleCheckout}
                className="px-6 py-3 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-bold text-white" data-testid="cart-checkout-btn">
                Ir al Checkout
              </button>
            </div>
            <p className="text-[10px] text-white/30 mt-3 text-center">Cada compra incluye: Musica (WAV+MP3) + Portada OG + Licencia Certificada</p>
          </div>
        )}

        {/* Track List — Spotify Style */}
        {loading ? (
          <div className="space-y-2">{[...Array(10)].map((_, i) => (
            <div key={i} className="h-14 bg-white/[0.03] rounded-lg animate-pulse" />
          ))}</div>
        ) : filteredTracks.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-white/40 mb-4">No se encontraron tracks</p>
            <button onClick={clearFilters} className="btn-secondary py-2 px-6 text-sm">Limpiar Filtros</button>
          </div>
        ) : (
          <div data-testid="track-list">
            {/* Header Row */}
            <div className="grid grid-cols-[40px_1fr_120px_80px_70px_90px_40px] gap-3 px-4 py-2 text-[10px] text-white/30 uppercase tracking-wider font-medium border-b border-white/5">
              <span>#</span><span>Titulo</span><span>Genero</span><span>BPM</span><span>Key</span><span>Precio</span><span></span>
            </div>
            {/* Track Rows */}
            {filteredTracks.map((track, idx) => (
              <div key={track.id}
                className={`group grid grid-cols-[40px_1fr_120px_80px_70px_90px_40px] gap-3 px-4 py-3 items-center rounded-lg transition-colors cursor-pointer ${
                  isInCart(track.id) ? 'bg-[#E8720C]/5 border-l-2 border-[#E8720C]' : 'hover:bg-white/[0.04]'
                }`}
                onClick={() => setSelectedTrack(track)}
                data-testid={`track-row-${track.id}`}>
                {/* # / Play */}
                <div className="flex items-center justify-center">
                  {track.audio_preview_url ? (
                    <button onClick={e => { e.stopPropagation(); togglePlay(track); }}
                      className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" data-testid={`play-${track.id}`}>
                      {playingId === track.id ? <Pause className="w-3.5 h-3.5 text-[#E8720C]" /> : (
                        <span className="group-hover:hidden text-xs text-white/30">{idx + 1}</span>
                      )}
                      {playingId !== track.id && <Play className="w-3.5 h-3.5 text-white/50 hidden group-hover:block" />}
                    </button>
                  ) : (
                    <span className="text-xs text-white/20">{idx + 1}</span>
                  )}
                </div>
                {/* Title + Artist */}
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${playingId === track.id ? 'text-[#E8720C]' : ''}`}>
                    {track.title}
                    {track.includes_playlist && <span className="ml-2 px-1.5 py-0.5 bg-[#E8720C]/15 text-[#E8720C] rounded text-[9px] font-bold align-middle">+ PLAYLISTS</span>}
                    {playingId === track.id && (
                      <span className="ml-2 px-1.5 py-0.5 bg-[#E8720C]/20 text-[#E8720C] rounded text-[9px] font-bold align-middle tracking-wider inline-flex items-center gap-1" data-testid={`preview-badge-${track.id}`}>
                        <Lock className="w-2.5 h-2.5" /> PREVIEW 1:00
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-white/40 truncate">{track.artist || 'OG MUSIC'}</p>
                </div>
                {/* Genre */}
                <span className="text-xs text-white/40 truncate">{track.genre}</span>
                {/* BPM */}
                <span className="text-xs text-white/30">{track.bpm || '-'}</span>
                {/* Key */}
                <span className="text-xs text-white/30">{track.key || '-'}</span>
                {/* Price */}
                <span className="text-sm font-semibold">
                  {track.discount_pct ? (
                    <>
                      <span className="text-white/30 line-through text-xs mr-1">${track.original_price}</span>
                      <span className="text-[#E8720C]">${track.price}</span>
                      <span className="ml-1 px-1 py-0.5 bg-[#E8720C]/15 text-[#E8720C] rounded text-[8px] font-bold">-{track.discount_pct}%</span>
                    </>
                  ) : (
                    <span className="text-[#E8720C]">${track.price}</span>
                  )}
                </span>
                {/* Add to Cart */}
                <button onClick={e => { e.stopPropagation(); addToCart(track); }}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    isInCart(track.id) ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/30 hover:bg-[#E8720C]/20 hover:text-[#E8720C]'
                  }`} data-testid={`add-cart-${track.id}`}>
                  {isInCart(track.id) ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        {!loading && hasMore && filteredTracks.length > 0 && (
          <div className="flex justify-center mt-8">
            <button onClick={loadMore} className="px-8 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white/60 flex items-center gap-2" data-testid="load-more-btn">
              <ChevronDown className="w-4 h-4" />Cargar Mas
            </button>
          </div>
        )}

        {/* Track Detail Popup */}
        {selectedTrack && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelectedTrack(null)} data-testid="track-detail-popup">
            <div className="glass-card p-6 w-full max-w-md border border-white/10" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">{selectedTrack.title}</h3>
                <button onClick={() => setSelectedTrack(null)} className="p-1 hover:bg-white/10 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3 mb-5">
                <div className="flex justify-between text-sm"><span className="text-white/40">Artista</span><span>{selectedTrack.artist || 'OG MUSIC'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-white/40">Genero</span><span>{selectedTrack.genre}</span></div>
                {selectedTrack.bpm && <div className="flex justify-between text-sm"><span className="text-white/40">BPM</span><span>{selectedTrack.bpm}</span></div>}
                {selectedTrack.key && <div className="flex justify-between text-sm"><span className="text-white/40">Key</span><span>{selectedTrack.key}</span></div>}
                <div className="flex justify-between text-sm"><span className="text-white/40">Precio</span>
                  <span>
                    {selectedTrack.discount_pct ? (
                      <>
                        <span className="text-white/30 line-through text-sm mr-2">${selectedTrack.original_price}</span>
                        <span className="text-[#E8720C] font-bold text-lg">${selectedTrack.price}</span>
                        <span className="ml-1.5 px-1.5 py-0.5 bg-[#E8720C]/15 text-[#E8720C] rounded text-xs font-bold">-{selectedTrack.discount_pct}%</span>
                      </>
                    ) : (
                      <span className="text-[#E8720C] font-bold text-lg">${selectedTrack.price}</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Preview notice */}
              <div className="flex items-center gap-2.5 px-3 py-2.5 mb-4 rounded-xl bg-[#E8720C]/5 border border-[#E8720C]/15" data-testid="popup-preview-notice">
                <Lock className="w-4 h-4 text-[#E8720C] shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold text-white/90">Escuchas un preview de 60 segundos</p>
                  <p className="text-[10px] text-white/50">Al comprar recibes la cancion completa en <strong className="text-[#E8720C]">WAV + MP3</strong></p>
                </div>
              </div>

              {/* Ecosystem included */}
              <div className="p-3 bg-[#E8720C]/5 border border-[#E8720C]/10 rounded-xl mb-5">
                <p className="text-xs font-semibold text-[#E8720C] mb-2">Incluido con tu compra:</p>
                <div className="grid grid-cols-2 gap-2">
                  {['Musica WAV + MP3', 'Portada OG Exclusiva', 'Licencia Certificada', 'Guia de Distribucion',
                    ...(selectedTrack.includes_playlist ? ['Acceso Ecosistema Playlists'] : [])
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-white/60">
                      <Check className="w-3 h-3 text-[#E8720C]" />{item}
                    </div>
                  ))}
                </div>
                {selectedTrack.includes_playlist && (
                  <p className="text-[10px] text-[#E8720C]/80 mt-2 font-medium">Al comprar, puedes enviarnos tu link de Spotify y metemos tu musica en nuestras playlists principales.</p>
                )}
                <p className="text-[10px] text-white/30 mt-2">Al comprar, genera tu Portada OG desde tu panel. Una portada unica, solo para ti.</p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => addToCart(selectedTrack)}
                  disabled={isInCart(selectedTrack.id)}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                    isInCart(selectedTrack.id) ? 'bg-green-500/20 text-green-400' : 'bg-[#E8720C] hover:bg-[#C55A00] text-white'
                  }`} data-testid="popup-add-cart">
                  {isInCart(selectedTrack.id) ? <><Check className="w-4 h-4" />En Carrito</> : <><ShoppingCart className="w-4 h-4" />Agregar al Carrito</>}
                </button>
                <button onClick={() => navigate(`/checkout?type=track&id=${selectedTrack.id}&license=basic`)}
                  className="px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white/60" data-testid="popup-buy-now">
                  Comprar Ahora
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogPage;
